import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { TokenService } from './token.service';
import { MsalService } from '@azure/msal-angular';
type ProfileType = {
  givenName?: string,
  surname?: string,
  userPrincipalName?: string,
  id?: string,
  displayName?: string,
  jobTitle?: string,
  officeLocation?: string
}
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  url: string;
  urlUserFilter: string;
  apiToken: string = "";
  userId: string = "";
  userObject: any;
  profile!: ProfileType;
  profileId: any;
  groupid: string = "";
  groupName: string = "";
  constructor(private http: HttpClient, private tokenService: TokenService, private msalService: MsalService) {
    this.url = environment.apiConfig.apiPath;
    this.urlUserFilter = environment.apiConfig.uri + environment.apiConfig.userIdFilter;
  }

  private getTokenHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${this.apiToken}`,
      'Content-Type': 'application/json'
    });
  };

  private createHeaders(): Observable<HttpHeaders> {
    return this.createHeadersOptions(false, 'application/json');
  }


  private createEncodedHeaders(token: string): Observable<HttpHeaders> {
    return this.createHeadersOptions(true, 'text/plain');
  }

  private createHeadersOptions(isEncodedHeaders: boolean, contentType: string): Observable<HttpHeaders> {
    return this.tokenService.getValidAccessToken().pipe(
      switchMap((token) => {
        this.apiToken = token ?? '';

        if (!this.apiToken) {
          return of(new HttpHeaders({
            'Content-Type': contentType
          }));
        }

        sessionStorage.setItem('token', this.apiToken);

        if (!isEncodedHeaders) {
          this.ensureUserAndGroup();
        }

        return of(new HttpHeaders({
          'Content-Type': contentType,
          'Authorization': `Bearer ${this.apiToken}`
        }));
      })
    );
  }

  private ensureUserAndGroup() {
    const userId = sessionStorage.getItem('userId');
    const groupId = sessionStorage.getItem('groupId');

    if (!userId) {
      this.setUser();
      return;
    }

    this.userId = userId;
    if (!groupId) {
      this.setGroupId().subscribe();
    }
  }

  private setUser() {
    this.userId = sessionStorage.getItem('userId') ?? "";
    if (sessionStorage.getItem('userId') === null || sessionStorage.getItem('userId') === undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiToken}`
      });
      this.http.get(this.urlUserFilter, { headers })
        .subscribe(response => {
          this.userObject = response;
          this.userId = this.userObject.employeeId ?? 0;
          sessionStorage.setItem('userId', this.userId);
          // Fetch group information immediately after resolving the logged-in user id.
          this.setGroupId().subscribe();
        });
      return;
    }

    if (sessionStorage.getItem('groupId') === null || sessionStorage.getItem('groupId') === undefined) {
      this.setGroupId().subscribe();
    }
  }


  public groupId(): Observable<string | null> {
    const cached = sessionStorage.getItem('groupId');
    if (cached) {
      return of(cached);
    }
    this.setUser();
    return this.setGroupId().pipe(
      map(() => sessionStorage.getItem('groupId'))
    );
  }

  private setGroupId(): Observable<any> {
    const cached = sessionStorage.getItem('groupId');
    if (cached) {
      this.groupid = cached;
      return of(cached);
    }

    const fallbackProfileId = this.msalService.instance.getActiveAccount()?.username ?? '';
    this.profileId = sessionStorage.getItem('useremail') || fallbackProfileId;

    if (!this.profileId) {
      return of(null);
    }

    return this.getAccessTokenAndCallGraphAP2().pipe(
      switchMap((accessToken) =>
        this.http.get<any>(
          environment.apiConfig.dirURI.replace('profileId', this.profileId),
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
              'ConsistencyLevel': 'eventual'
            }
          }
        )
      ),
      map((data) => data?.value?.[0]),
      tap((group) => {
        if (group?.id) {
          this.groupid = group.id;
          this.groupName = group.displayName ?? '';
          sessionStorage.setItem('groupId', group.id);
        }
      }),
      catchError((error) => {
        console.error('Failed to resolve groupId', error);
        return of(null);
      })
    );
  }


  public getUser() {
    if (sessionStorage.getItem('userId') === null || sessionStorage.getItem('userId') === undefined) {
      this.setUser();
      this.setGroupId();
    }
    return sessionStorage.getItem('userId');
  }

  public get(url: string): Observable<any> {
    return this.createHeaders().pipe(
      switchMap((headers) => {
        return this.http.get(url, { headers }).pipe(
          catchError((error) => {
            console.error('Error occurred while fetching data:', error);
            throw error;
          })
        );
      })
    )
  }

  public post(url: string, body: any): Observable<any> {
    return this.createHeaders().pipe(
      switchMap((headers) => {
        return this.http.post(url, body, { headers }).pipe(
          catchError((error) => {
            console.error('Error occurred while posting data:', error);
            throw error;
          })
        );
      })
    )
  }

  public postEncoded(url: string, body: any): Observable<any> {
    return this.createEncodedHeaders('null').pipe(
      switchMap((headers) => {
        return this.http.post(url, body, { headers }).pipe(
          catchError((error) => {
            console.error('Error occurred while posting data:', error);
            throw error;
          })
        );
      })
    )
  }

  public put(url: string, body: any): Observable<any> {
    return this.createHeaders().pipe(
      switchMap((headers) => {
        return this.http.put(url, body, { headers }).pipe(
          catchError((error) => {
            console.error('Error occurred while posting data:', error);
            throw error;
          })
        );
      })
    )

  }

  getAccessTokenAndCallGraphAP2() {
    return this.msalService.acquireTokenSilent({
      scopes: ['Group.Read.All', 'User.Read.All'],
    }).pipe(map(result => {
      sessionStorage.setItem('groupAccessToken', result.accessToken);
      return result.accessToken;
    })
    )
  }
}
